/**
 * Nine tiers or five themes (roadmap D161, restored in D340 phase 3). The grouping is one switch that every picture of the
 * Memory control follows: the globe's shells, the map's rings and hubs, the Timeline's lanes, the shelves and the legend.
 *
 * Nine colours carrying meaning is more than eyes tell apart (two pairs fail the colour-vision checks), so the same nine
 * tiers can be read as **five themes**: Direction (roadmap, plans), Governance, Structure (architecture, library), the
 * agent's kit (tools, routines, skills) and Memory. A theme has one tone, in an order that passes the checks on the night
 * stage; a tier under it is a word, never a tenth colour. Restored from `armsLanes.ts` (0.45.0), which the ring canvas
 * took with it; the ring geometry is not restored, the grouping is.
 *
 * DOM-free (test/armsGrouping.test.ts).
 */
import { ARMS_TIERS, type ArmsTierId } from './armsModel.js';

/** One ring, shell, hub or shelf per tier, or one per theme. */
export type ArmsGrouping = 'tiers' | 'themes';
export const GROUPINGS: ReadonlyArray<{ id: ArmsGrouping; label: string; hint: string }> = [
  { id: 'tiers', label: 'Nine tiers', hint: 'Every layer on its own: roadmap, architecture, plans, governance, library, tools, routines, memory, skills.' },
  { id: 'themes', label: 'Five themes', hint: 'The same layers in five groups: direction, governance, structure, the agent’s kit and memory.' },
];

export type ThemeId = 'direction' | 'governance' | 'structure' | 'kit' | 'memory';

export interface ArmsTheme {
  id: ThemeId;
  title: string;
  /** Validated for the night stage in this order: rose beside green fails, so Governance sits between them. */
  colour: string;
  tiers: readonly ArmsTierId[];
}

/** The five themes, outermost first: what is planned, what was decided, where it lands, what the agent has, what it remembers. */
export const ARMS_THEMES: readonly ArmsTheme[] = [
  { id: 'direction', title: 'Direction', colour: '#3fa85e', tiers: ['roadmap', 'plans'] },
  { id: 'governance', title: 'Governance', colour: '#3690e3', tiers: ['decisions'] },
  { id: 'structure', title: 'Structure', colour: '#e95979', tiers: ['architecture', 'library'] },
  { id: 'kit', title: "Agent's kit", colour: '#14a08d', tiers: ['apps', 'routines', 'skills'] },
  { id: 'memory', title: 'Memory', colour: '#9769dc', tiers: ['memory'] },
];

/** The theme a tier belongs to. */
export function themeOfTier(tier: ArmsTierId): ArmsTheme {
  return ARMS_THEMES.find((t) => t.tiers.includes(tier))!;
}

/** The colour a tier is drawn in under a grouping: its own, or its theme's. */
export function groupColour(grouping: ArmsGrouping, tier: ArmsTierId): string {
  return grouping === 'themes' ? themeOfTier(tier).colour : (ARMS_TIERS.find((t) => t.id === tier)?.colour ?? '#8a8f98');
}

/** One entry of the legend, a shelf group or a lane group: a tier, or a theme with its tiers. */
export interface ArmsGroup {
  id: string;
  title: string;
  colour: string;
  tiers: readonly ArmsTierId[];
  /** The theme it stands for, under five themes. */
  theme?: ThemeId;
}

/** The groups of a grouping, outermost first: nine tiers, or five themes. */
export function armsGroups(grouping: ArmsGrouping): ArmsGroup[] {
  if (grouping === 'tiers') return ARMS_TIERS.map((t) => ({ id: t.id, title: t.title, colour: t.colour, tiers: [t.id] }));
  return ARMS_THEMES.map((t) => ({ id: t.id, title: t.title, colour: t.colour, tiers: t.tiers, theme: t.id }));
}

/** What the control remembers of the grouping, trusting none of it. */
export function parseGrouping(v: unknown): ArmsGrouping {
  return v === 'themes' ? 'themes' : 'tiers';
}
